import {Injectable} from '@angular/core';
import {Observable, zip} from "rxjs";
import {filter, tap} from "rxjs/operators";
import {AppService} from "./app.service";
import {Store} from "@ngrx/store";
import {AppState} from "../store/store";
import {ProfileState} from "../store/profile/profile";
import {DataSourceDimensionsState} from '../store/dataSourceDimensions/dataSourceDimensions';
import {DataSourcesMetricsState} from "../store/dataSourcesMetrics/dataSourcesMetrics";
import {ChannelsState} from "../store/channels/channels";
import {SpacesState} from "../store/spaces/spaces";
import {InitState} from "../store/init/init";
import {initApp} from "../store/init/init.actions";
import {selectInitState} from "../store/init/init.selectors";
import {selectSpacesState} from "../store/spaces/spaces.selectors";
import {selectDataSourceDimensionsState} from '../store/dataSourceDimensions/dataSourceDimensions.selectors';
import {selectDataSourcesMetricsState} from "../store/dataSourcesMetrics/dataSourcesMetrics.selectors";
import {selectChannelsState} from "../store/channels/channels.selectors";
import {selectProfileState} from "../store/profile/profile.selectors";
import {LanguagesState} from "../store/languages/languages";
import {selectLanguagesState} from "../store/languages/languages.selectors";
import {selectDimensionComponentObjects} from '../store/selectors';
import {DimensionComponentObjects} from '../classes/dimension-component-object';
import {selectDataSetMetricComponentObjects} from '../store/dataSetMetrics/dataSetMetrics.selectors';
import {MetricComponentObjects} from '../classes/metric-component-object';

@Injectable({
  providedIn: 'root'
})
export class InitService {

  private _spaces$: Observable<SpacesState> = this._store.select(selectSpacesState);
  private _profile$: Observable<ProfileState> = this._store.select(selectProfileState);
  private _dimensions$: Observable<DimensionComponentObjects> = this._store.select(selectDimensionComponentObjects);
  private _dataSourcesDimensions$: Observable<DataSourceDimensionsState> = this._store.select(selectDataSourceDimensionsState);
  private _metrics$: Observable<MetricComponentObjects> = this._store.select(selectDataSetMetricComponentObjects);
  private _dataSourcesMetrics$: Observable<DataSourcesMetricsState> = this._store.select(selectDataSourcesMetricsState);
  private _channels$: Observable<ChannelsState> = this._store.select(selectChannelsState);
  private _init$: Observable<InitState> = this._store.select(selectInitState);
  private _languages$: Observable<LanguagesState> = this._store.select(selectLanguagesState);

  constructor(
    private readonly _appS: AppService,
    private readonly _store: Store<AppState>
  ) {}

  public init(): void {
    this._store.dispatch(initApp());
  }

  public changes(): Observable<any> {
    return zip([
      this._languages$.pipe(
        tap((state: LanguagesState) => {
          this._appS.lang = state.language?.attributes.code
        })
      ),
      this._init$.pipe(
        filter(state => state.logged),
        tap((initState: InitState) => {
          this._appS.datasets = initState.sites;
          this._appS.space = initState.space;
          this._appS.dataset = initState.site;
        })
      ),
      this._spaces$.pipe(
        tap((spacesState: SpacesState) => {
          this._appS.spaces = spacesState.spaces;
        })
      ),
      this._profile$.pipe(
        tap((profileState: ProfileState) => {
          this._appS.user = profileState.profile;
        })
      ),
      this._dimensions$.pipe(
        tap((dimensions: DimensionComponentObjects) => {
          this._appS.dimensions = dimensions;
        })
      ),
      this._dataSourcesDimensions$.pipe(
        tap((dataSourcesDimensionsState: DataSourceDimensionsState) => {
          this._appS.dataSourcesDimensions = dataSourcesDimensionsState.componentObjects;
        })
      ),
      this._metrics$.pipe(
        tap((metrics: MetricComponentObjects) => {
          this._appS.metrics = metrics;
        })
      ),
      this._dataSourcesMetrics$.pipe(
        tap((dataSourcesMetricsState: DataSourcesMetricsState) => {
          this._appS.dataSourcesMetrics = dataSourcesMetricsState.componentObject;
        })
      ),
      this._channels$.pipe(
        tap((channelsState: ChannelsState) => {
          this._appS.channels = channelsState.channels;
        })
      )
    ]);
  }
}
